import { useMemo, useState, useEffect } from "react";
import Accordion from 'react-bootstrap/Accordion';
import {SetMetaData} from './../../helper/page-title';
import ValidationHelper from "../../helper/validation";
import useInfiniteList from "../../hooks/infinite-list";
import useRowSelection from "../../hooks/row-selection";
import useColumnSettings from "../../hooks/column-setting";
import InfiniteListWrapper from "../../components/wrapper/infinite-list";
import { error_api_call, post_api_call, get_api_call, downloadSampleFile } from "../../actions/action-creators/common";
import { alertResponseMessage } from "../../actions/action-creators/response";
import FormDataEsg from "../../data/formData/esg/student-level";
import FilterFloatMaster from "../../components/form/filter/filter-float";
import {SettingDropdownUi, TableCheckboxUi, RowDataUi} from "../../components/form/master-table/ui-static";
import MasterForm  from '../../components/form/master';
import normalizeFilters from "../../helper/filter/normalize-filter";
import EsgStudentLevelExcelModal from "../../modal/esg/student-level-excel";

const {
    masterTableFields,
    filterTableData,
    fieldValuesLevel,
    fieldUpdateLevel,
    fieldValidationLevel
} = FormDataEsg;

const PAGE_LIMIT = 50;
const STATUS_BADGE = {active: "badge-success", completed: "badge-info", dropped: "badge-danger"};

// Splits the ids/emails text into unique tokens (commas, spaces or new lines).
const parseTokens = (text) => Array.from(new Set(String(text || "").split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean)));

function EsgStudentLevel(props) {
    SetMetaData("ESG Student Levels", "", "");
    const [filterData, setFilterData] = useState({});
    const [tmpFilterData, setTmpFilterData] = useState({});
    const [errorData, setErrorData] = useState({update_level: []});
    const [successData, setSuccessData] = useState({update_level: []});
    const [formLevelData, setLevelFormData] = useState({...fieldValuesLevel});
    const [isProcess, setIsProcess] = useState({update_level: false});
    const [refreshKey, setRefreshKey] = useState(0);
    const [excelModal, setExcelModal] = useState(false);

    const { columnSettings, updateColumn } = useColumnSettings({
        user_id: true,
        name: true,
        email: true,
        mobile_number: true,
        school_id: true,
        branch_id: true,
        class_id: true,
        esg_levels: true
    });

    const [optionData, setOptionData] = useState({
        esg_level_id: [],
        school_id: [],
        branch_id: [],
        class_id: []
    });

    const normalizedFilter = useMemo(
        () => normalizeFilters(filterData, {
            esg_level_id: "id",
            school_id: "id",
            branch_id: "id",
            class_id: "id"
        }),
        [filterData]
    );

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteList({
        queryKey: ["esg_student_levels", refreshKey],
        apiCall: payload => post_api_call("esg-student-levels", payload),
        limit: PAGE_LIMIT,
        extraPayload: {
            filter: normalizedFilter
        },
        // Keep loading pages until every student (total) has been fetched.
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce((sum, page) => sum + (Array.isArray(page?.data) ? page.data.length : 0), 0);
            return loaded < (lastPage?.total ?? 0) ? allPages.length + 1 : undefined;
        }
    });

    const masterTableData = Array.isArray(data?.pages)
                            ? data.pages.flatMap(page => Array.isArray(page?.data) ? page.data : [])
                            : [];

    const tokens = parseTokens(formLevelData.ref_ids);
    const selectedUserIds = tokens.filter(t => !t.includes("@"));

    const {
        isChecked,
        isAllSelected,
        isIndeterminate,
        toggleRow,
        selectAllVisible,
        clearAllVisible
    } = useRowSelection({
        data: masterTableData,
        rowKey: "user_id",
        value: selectedUserIds.map(String),
        maxSelection: 300,
        onLimitExceed: limit => {
            alertResponseMessage({
                alert_type: 3,
                message: `You can select maximum ${limit} students at once.`
            });
        },
        onChange: ids => {
            // Row selection replaces the typed user ids but keeps any emails typed in.
            const emails = parseTokens(formLevelData.ref_ids).filter(t => t.includes("@"));
            const merged = [...ids.map(String), ...emails];
            setLevelFormData(prev => ({
                ...prev,
                ref_ids_array: merged,
                ref_ids: merged.join(",")
            }));
        }
    });

    //Get Data from APIs
    const fetchSelectList = async(type, url) => {
      try{
          const selectApiData = await get_api_call(url, {});
          if(selectApiData.status === 200){
              const apiData = selectApiData.data;
              switch (type) {
                  case 'esg_student_level':
                    setOptionData(prev => ({...prev,
                        esg_level_id: apiData?.esg_years,
                        school_id: apiData?.schools,
                        branch_id: apiData?.branches,
                        class_id: apiData?.classes
                    }));
                    break;
                  default:
              }
          }
      }catch(e){
        error_api_call({error: `admin|screens|esg|student-level|fetchSelectList|${e}`});
      }
    };

    useEffect(() => {
        fetchSelectList('esg_student_level', 'dropdown-options/esg_years,schools,branches,classes');
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const updateInput = (form_type, attrName, value) => {
        try {
            if(form_type === "update_level") {
                if(errorData[form_type].length){
                    setErrorData({...errorData, [form_type]: []});
                }
                if(successData[form_type].length){
                    setSuccessData({...successData, [form_type]: []});
                }
                if (attrName === "ref_ids") {
                    setLevelFormData(prev => ({
                        ...prev,
                        ref_ids: value,
                        ref_ids_array: parseTokens(value)
                    }));
                }else{
                    setLevelFormData(prev => ({...prev, [attrName]: value}));
                }
            }else if(form_type === "filter"){
                setTmpFilterData(prev => ({...prev, [attrName]: value}));
            }
        } catch (e) {
            console.log(e);
            error_api_call({error: `admin|screens|esg|student-level|updateInput|${e}`});
        }
    };

    //Submit Details
    const submitForm = async(e, form_type='') => {
        try{
            e.preventDefault();
            setErrorData({...errorData, [form_type]: []});
            setSuccessData({...successData, [form_type]: []});
            setIsProcess({...isProcess, [form_type]: true});
            const validation = ValidationHelper.validation({...formLevelData}, fieldValidationLevel).error;
            if(validation.length){
                setErrorData({...errorData, [form_type]: validation});
                setIsProcess({...isProcess, [form_type]: false});
                return;
            }
            const identifiers = parseTokens(formLevelData.ref_ids);
            if(identifiers.length > 300){
                setErrorData({...errorData, [form_type]: ["Maximum 300 students at once."]});
                setIsProcess({...isProcess, [form_type]: false});
                return;
            }
            const res = await post_api_call('esg-student-level/update', {
                identifiers,
                level: String(formLevelData.level_id?.id)
            });
            if(res.status === 200){
                const results = res.results || [];
                const failed = results.filter(r => r.result === 'failed');
                const succeeded = results.filter(r => r.result !== 'failed');
                const changed = succeeded.filter(r => r.result !== 'unchanged');
                if(failed.length === 0){
                    // Everything worked (or already existed): just the toast, no box.
                    alertResponseMessage(changed.length
                        ? {alert_type: 1, message: "Information Updated Successfully"}
                        : {alert_type: 3, message: "No modification needed, data already exists."});
                }else{
                    // All failed: red box only. Partly failed: red box, then a green box for the rest.
                    setErrorData({...errorData, [form_type]: failed.map(r => `${r.identifier}: ${r.message}`)});
                    if(succeeded.length){
                        setSuccessData({...successData, [form_type]: succeeded.map(r => `${r.identifier}: ${r.message}`)});
                    }
                }
                if(changed.length){
                    clearAllVisible();
                    setLevelFormData({...fieldValuesLevel, level_id: formLevelData.level_id});
                    setRefreshKey(prev => prev + 1); // reset table
                }
            }else if(res.errors){
                setErrorData({...errorData, [form_type]: Object.values(res.errors).flat()});
            }else if(typeof res.message !== "undefined"){
                setErrorData({...errorData, [form_type]: [res.message]});
            }
            setIsProcess({...isProcess, [form_type]: false});
        }catch(error){
            console.error(error);
            setErrorData({...errorData, [form_type]: ['Something Went Wrong, Please Try After Some Time']});
            setIsProcess({...isProcess, [form_type]: false});
            error_api_call({error: `admin|screens|esg|student-level|submitForm|${error}`});
        }
    };

    const renderCell = (col, row) => (
        col.fetch_key === "esg_levels"
            ? ((row.esg_levels || []).length > 0
                ? row.esg_levels.map((l, i) => (
                    <span key={i} className={`badge ${STATUS_BADGE[l.status] || "badge-secondary"} me-1`} title={l.status}>{l.code}</span>
                ))
                : <span className="text-muted">-</span>)
            : <RowDataUi fetch_key={col.fetch_key} data={row} />
    );

    return (
        <div className="row">
            {/* Excel + template (same as Manage Students) */}
            <div className="col-lg-12 text-end">
                <div className="d-grid float-end">
                    <button className="btn btn-info mb-2 p-2" onClick={() => setExcelModal(true)}>Update Levels (Excel)</button>
                    <button className="btn btn-link p-0 mb-2" onClick={(e) => downloadSampleFile(e, 'esg_student_level', 'sample/esg-student-level', 'Template-ESG-Student-Level')}>Template File</button>
                </div>
            </div>

            <div className="col-lg-12">
                <div className="card p-2 mb-2 mod-custom z-index-99999">
                    <Accordion className="filter-custom">
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>Update ESG Student Level</Accordion.Header>
                            <Accordion.Body className="p-2">
                                <MasterForm
                                    formType={'update_level'}
                                    data={formLevelData}
                                    _errorData={{...errorData}}
                                    _successData={{...successData}}
                                    _isProcess={{...isProcess}}
                                    _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, field, value)}
                                    _submitForm={(e, form_type) => submitForm(e, form_type)}
                                    _formData={[{form_type: 'update_level', label: "ESG Level", fields: [...fieldUpdateLevel]}]}
                                    selectOptions={{level_id: {
                                                        value: 'id',
                                                        label: 'label',
                                                        data: [...(optionData.esg_level_id || [])],
                                                    }}}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
            </div>

            {/* Filter (button + floating panel, same concept as Filter Auditors) */}
            <div className="col-lg-12">
                <div className="card p-2 mb-2">
                    <FilterFloatMaster
                        title="Filter Students"
                        formType="filter"
                        data={tmpFilterData}
                        _isProcess={{}}
                        _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, field, value)}
                        _formData={[{label: "Filter Students:", display_type: 'col', fields: [...filterTableData]}]}
                        _submitForm = {(e) => {e.preventDefault(); setFilterData({...tmpFilterData}); clearAllVisible();}}
                        _is_download={false}
                        selectOptions={{
                            esg_level_id: {value: 'id', label: 'label', data: [...(optionData.esg_level_id || [])]},
                            school_id: {value: 'id', label: 'school_name', data: [...(optionData.school_id || [])]},
                            branch_id: {value: 'id', label: 'branch_name', data: [...(optionData.branch_id || [])]},
                            class_id: {value: 'id', label: 'class_name', data: [...(optionData.class_id || [])]}
                        }}
                        _resetFilterForm = {(e) => {e.preventDefault(); setTmpFilterData({}); setFilterData({}); clearAllVisible();}}
                    />
                </div>
            </div>

            <InfiniteListWrapper
                dataLength={masterTableData.length}
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                emptyMessage="No Student found!"
            >
                <div className="col-lg-12 grid-margin stretch-card">
                    <div className="card">
                        <SettingDropdownUi
                            checkedData={columnSettings}
                            fields={masterTableFields}
                            updateColumn= {(field, value) => updateColumn(field, value)}
                        />

                        <div className="card-body table-responsive sticky-table pt-0 pe-0 ps-3">
                            <table className="table">
                                <thead>
                                <tr>
                                    <th>
                                        <TableCheckboxUi
                                            id="select_row"
                                            name="select_row"
                                            checked={isAllSelected}
                                            ref={el => el && (el.indeterminate = isIndeterminate)}
                                            onChange={(e) => e.target.checked ? selectAllVisible() : clearAllVisible()}
                                        />
                                    </th>
                                    {masterTableFields.map((col, i) => (
                                        columnSettings[col.field.name] && (
                                            <th key={i} className={`w-200p ${col.class_name || ""}`}>{col.title}</th>
                                        )
                                    ))}
                                </tr>
                                </thead>

                                <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="20" className="text-center p-4">
                                            <img alt="Loader" src={require("../../assets/images/Image-Loader.gif")} />
                                        </td>
                                    </tr>
                                ) : (
                                    masterTableData.length > 0
                                    ? masterTableData.map((row) => (
                                        <tr
                                            key={row.user_id}
                                            className={isChecked(row.user_id) ? "table-success" : ""}
                                            onClick={() => toggleRow(row.user_id)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td onClick={e => e.stopPropagation()}>
                                                <TableCheckboxUi
                                                    id={row.user_id}
                                                    name="select_row"
                                                    checked={isChecked(row.user_id)}
                                                    onChange={() => toggleRow(row.user_id)}
                                                />
                                            </td>
                                            {masterTableFields.map((col, i) => (
                                                columnSettings[col.field.name] && (
                                                    <td key={i}>{renderCell(col, row)}</td>
                                                )
                                            ))}
                                        </tr>
                                    ))
                                    : (
                                    <tr>
                                        <td colSpan="20">
                                            <div className="no-fellow text-center mt-4">
                                                <p className="pt-4 head">{"No Record Found!"}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    )
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </InfiniteListWrapper>

            <EsgStudentLevelExcelModal
                isShow={excelModal}
                _toggleModal={(e, state) => setExcelModal(state)}
                _resetListing={() => setRefreshKey(prev => prev + 1)}
            />
        </div>
    );
}

export default EsgStudentLevel;
